import { ReactNode, useRef, useState } from 'react';
import { IoImageOutline } from 'react-icons/io5';
import { TbArrowBarToDown, TbArrowBarToUp, TbGripVertical } from 'react-icons/tb';

import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Box, Button, Card, Center, Group, Stack, Text, Title, Tooltip } from '@mantine/core';

import { File } from 'models';

import { IKImage } from 'components/ik-image/IKImage';
import { useAction } from 'hooks/use-action';
import { LocalNotificationService } from 'services/LocalNotificationService';

type SortableItem = { id?: string };

const idOf = (item: SortableItem) => item.id!;

const SortableList = <T extends SortableItem>({
    items,
    onReorder,
    renderItem,
}: {
    items: T[];
    onReorder: (items: T[]) => Promise<unknown>;
    renderItem: (item: T) => ReactNode;
}) => {
    const itemsById = new Map(items.map((item) => [idOf(item), item]));

    const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);
    const latestSave = useRef(0);

    const orderedItems = pendingOrder
        ? pendingOrder.map((id) => itemsById.get(id)).filter((item): item is T => !!item)
        : items;
    const orderedIds = orderedItems.map(idOf);

    const reorder = async (ids: string[]) => {
        const saveId = ++latestSave.current;
        setPendingOrder(ids);

        const saved = await onReorder(ids.map((id) => itemsById.get(id)).filter((item): item is T => !!item));

        if (saveId !== latestSave.current) {
            return;
        }

        if (saved) {
            LocalNotificationService.showSuccess({ message: 'Order saved' });
        } else {
            LocalNotificationService.showError({ message: 'Could not save the new order' });
        }

        setPendingOrder(null);
    };

    const sensors = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = orderedIds.indexOf(active.id as string);
        const newIndex = orderedIds.indexOf(over.id as string);

        const isMissingFromList = oldIndex === -1 || newIndex === -1;
        if (isMissingFromList) {
            return;
        }

        reorder(arrayMove(orderedIds, oldIndex, newIndex));
    };

    const moveToTop = (id: string) => {
        reorder([id, ...orderedIds.filter((other) => other !== id)]);
    };

    const moveToBottom = (id: string) => {
        reorder([...orderedIds.filter((other) => other !== id), id]);
    };

    return (
        <Stack gap="xs">
            <Group gap={6} c="dimmed" wrap="nowrap">
                <TbGripVertical size={16} style={{ flexShrink: 0 }} />
                <Text size="xs">Drag to reorder. The top item shows first on your profile.</Text>
            </Group>

            <DndContext sensors={[sensors]} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                    <Stack gap="sm">
                        {orderedItems.map((item) => (
                            <SortableRow
                                key={idOf(item)}
                                id={idOf(item)}
                                onMoveToTop={moveToTop}
                                onMoveToBottom={moveToBottom}
                            >
                                {renderItem(item)}
                            </SortableRow>
                        ))}
                    </Stack>
                </SortableContext>
            </DndContext>
        </Stack>
    );
};

const SortableRow = ({
    id,
    onMoveToTop,
    onMoveToBottom,
    children,
}: {
    id: string;
    onMoveToTop: (id: string) => void;
    onMoveToBottom: (id: string) => void;
    children: ReactNode;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    return (
        <Card
            ref={setNodeRef}
            withBorder
            p="sm"
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
                zIndex: isDragging ? 1 : undefined,
            }}
        >
            <Group gap="md" wrap="nowrap" align="center">
                <Group gap={4} wrap="nowrap" align="center">
                    <Box
                        {...attributes}
                        {...listeners}
                        style={{ cursor: 'grab', touchAction: 'none', display: 'flex' }}
                        c="dimmed"
                        aria-label="Drag to reorder"
                    >
                        <TbGripVertical size={20} />
                    </Box>
                    <Tooltip label="Send to top">
                        <ActionIcon
                            size="lg"
                            variant="subtle"
                            color="gray"
                            onClick={() => onMoveToTop(id)}
                            aria-label="Send to top"
                        >
                            <TbArrowBarToUp size={18} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Send to bottom">
                        <ActionIcon
                            size="lg"
                            variant="subtle"
                            color="gray"
                            onClick={() => onMoveToBottom(id)}
                            aria-label="Send to bottom"
                        >
                            <TbArrowBarToDown size={18} />
                        </ActionIcon>
                    </Tooltip>
                </Group>

                {children}
            </Group>
        </Card>
    );
};

const SortableListItem = ({
    title,
    badge,
    description,
    details,
    image,
    placeholder,
    round,
    actions,
    onEdit,
    onDelete,
    deleting,
}: {
    title: string;
    badge?: ReactNode;
    description?: ReactNode;
    details?: ReactNode;
    image?: File | string | null;
    placeholder?: ReactNode;
    round?: boolean;
    actions?: ReactNode;
    onEdit?: () => void;
    onDelete?: () => Promise<void> | void;
    deleting?: boolean;
}) => {
    const [handleDelete, runningDelete] = useAction(async () => onDelete?.());

    return (
        <>
            <Box w={72} h={72} style={{ flexShrink: 0, overflow: 'hidden', borderRadius: round ? '50%' : 8 }}>
                {image ? (
                    <IKImage
                        fileOrId={image}
                        alt={title}
                        width={144}
                        height={144}
                        w="100%"
                        h="100%"
                        fit={round ? 'cover' : 'contain'}
                    />
                ) : (
                    (placeholder ?? (
                        <Center w="100%" h="100%" bg="var(--mantine-color-default-hover)" opacity={0.6}>
                            <IoImageOutline size={28} />
                        </Center>
                    ))
                )}
            </Box>

            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Group gap="xs" wrap="nowrap">
                    <Title order={3} size="h4" fw={700} lineClamp={1} style={{ minWidth: 0 }}>
                        {title}
                    </Title>
                    {badge}
                </Group>
                {description && (
                    <Text size="sm" c="dimmed" lineClamp={2}>
                        {description}
                    </Text>
                )}
                {details}
            </Stack>

            {(actions || onEdit || onDelete) && (
                <Group gap={8} wrap="nowrap">
                    {actions}
                    {onEdit && (
                        <Button size="xs" variant="outline" onClick={onEdit}>
                            Edit
                        </Button>
                    )}
                    {onDelete && (
                        <Button
                            size="xs"
                            variant="light"
                            color="red"
                            onClick={handleDelete}
                            loading={deleting || runningDelete}
                        >
                            Delete
                        </Button>
                    )}
                </Group>
            )}
        </>
    );
};

SortableList.Item = SortableListItem;

export { SortableList };
